"use client"

import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "~/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Skeleton } from "~/components/ui/skeleton";
import { Button } from "~/components/ui/button";

type Props<T extends string> = {
    selectedValue: T;
    onSelectedValueChange: (value: T) => void;
    searchValue: string;
    onSearchValueChange: (value: string) => void;
    items: { value: T; label: string; renderItem?: React.ReactNode | React.ReactNode[] }[];
    isLoading?: boolean;
    emptyMessage?: string;
    placeholder?: string;
    resetSearchOnSelectItem?: boolean;
    className?: string;
    popoverClassName?: string;
};

export function AutoComplete<T extends string>({
    selectedValue,
    onSelectedValueChange,
    searchValue,
    onSearchValueChange,
    items,
    isLoading,
    emptyMessage = "Keine Elemente gefunden.",
    placeholder = "Suchen...",
    resetSearchOnSelectItem = false,
    className = '',
    popoverClassName,
}: Props<T>) {
    const [open, setOpen] = useState(false);

    const labels = useMemo(
        () =>
            items.reduce((acc, item) => {
                acc[item.value] = item.label;
                return acc;
            }, {} as Record<string, string>),
        [items]
    );

    const reset = () => {
        onSelectedValueChange("" as T);
        onSearchValueChange("");
    };

    const onSelectItem = (inputValue: string) => {
        if (inputValue === selectedValue) {
            reset();
        } else {
            onSelectedValueChange(inputValue as T);

            if(resetSearchOnSelectItem) {
                onSearchValueChange("");
            } else {
                onSearchValueChange(labels[inputValue] ?? "");
            }
        }
        setOpen(false);
    };

    const selectedLabel = selectedValue ? labels[selectedValue] : "";

    return (
        <Popover modal={true} open={open} onOpenChange={(newOpen) => {
            setOpen(newOpen);
            if (newOpen) {
                onSearchValueChange("");
            }
        }}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={`w-full justify-between ${className}`}
                    onClick={() => setOpen(!open)}
                >
                    <span className="truncate">{selectedValue ? selectedLabel : placeholder}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className={cn("w-[var(--radix-popover-trigger-width)] p-0", popoverClassName)}>
                <Command shouldFilter={false}>
                    <CommandInput 
                        placeholder={placeholder}
                        value={searchValue}
                        onValueChange={onSearchValueChange}
                    />
                    <CommandList>
                        {isLoading && (
                            <div className="p-1">
                                <Skeleton className="h-6 w-full" />
                            </div>
                        )}
                        {!isLoading && items.length === 0 && (
                            <CommandEmpty>{emptyMessage}</CommandEmpty>
                        )}
                        {items.length > 0 && !isLoading && (
                            <CommandGroup>
                                {items.map((option) => (
                                    <CommandItem
                                        key={option.value}
                                        value={option.value}
                                        onSelect={onSelectItem}
                                        className={option.renderItem ? "items-start whitespace-normal" : undefined}
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 mt-0.5 h-4 w-4 shrink-0",
                                                selectedValue === option.value
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                            )}
                                        />
                                        <span className="min-w-0 flex-1">
                                            {option.renderItem || option.label}
                                        </span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}